import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { EditLayer, Field, LayerButtons, Notice, OnOffBadge, RowMenu, SkeletonList, Switch } from '@/admin/AdminShell'
import { fetchAccessUsers } from '@/admin/api'
import { ConfirmModal, StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  createMailbox,
  deleteMailbox,
  fetchMailboxes,
  pollMailbox,
  updateMailbox,
  type Mailbox,
  type MailboxKind,
  type MailboxSecurity,
  type PollResult,
} from './inboxApi'

/**
 * IMAP-ящики, которые CRM опрашивает (тикет #121): общие (sales@) и личные.
 *
 * Своё право — `system.integration`, как у SMTP-сервера: ящик это учётные данные. Пароль
 * только записывается: сервер отвечает, задан ли он, но не какой; пустое поле при сохранении
 * оставляет прежний.
 *
 * Состояние опроса показано словами: «сервер отказал» (неверный пароль — ящик не
 * опрашивается, пока его не сохранят заново) и «недоступен, повтор в …» — разные вещи, и
 * свести их в одно «ошибка» значило бы отправить администратора чинить не то.
 */
export function MailboxesPanel() {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const mayRead = can(user, 'system.integration', 'read')
  const mayEdit = can(user, 'system.integration', 'update')
  const boxes = useQuery({ queryKey: ['admin-mailboxes'], queryFn: ({ signal }) => fetchMailboxes(signal), enabled: mayRead })
  const [dialog, setDialog] = useState<Mailbox | 'new' | null>(null)
  const [removing, setRemoving] = useState<Mailbox | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [polled, setPolled] = useState<{ name: string; result: PollResult } | null>(null)

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin-mailboxes'] })
  const fail = (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error'))
  const poll = useMutation({
    mutationFn: (box: Mailbox) => pollMailbox(box.id).then((result) => ({ name: box.name, result })),
    onSuccess: (value) => {
      setNotice(null)
      setPolled(value)
      refresh()
      void queryClient.invalidateQueries({ queryKey: ['mail-inbox'] })
    },
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: (box: Mailbox) => deleteMailbox(box.id),
    onSuccess: () => {
      setRemoving(null)
      refresh()
    },
    onError: (error) => {
      setRemoving(null)
      fail(error)
    },
  })

  if (!mayRead) {
    return <StateView kind="forbidden" compact title={t('mailbox.forbidden')} description={t('mail.conn.forbidden.text')} />
  }
  const list = boxes.data ?? []
  return (
    <section className="panel stack" style={{ gap: 12, marginBottom: 16 }} aria-label={t('mailbox.title')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('mailbox.title')}</span>
        <span className="spacer" />
        {mayEdit && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDialog('new')}>{t('mailbox.add')}</button>
        )}
      </div>
      <span className="t-sm muted">{t('mailbox.lead')}</span>
      <Notice text={notice} />
      {polled && (
        <span className="t-sm" role="status">
          {t(`mailbox.poll.${polled.result.status}`, {
            name: polled.name,
            received: polled.result.received,
            unmatched: polled.result.unmatched,
            error: polled.result.error ?? '',
          })}
        </span>
      )}
      {boxes.isLoading ? <SkeletonList rows={2} /> : boxes.error ? (
        <StateView kind="error" compact title={t('mailbox.error')} />
      ) : list.length === 0 ? (
        <StateView kind="empty" compact title={t('mailbox.empty')} description={t('mailbox.empty.text')} />
      ) : (
        <table className="table" aria-label={t('mailbox.title')}>
          <thead>
            <tr>
              <th>{t('mailbox.name')}</th>
              <th>{t('mailbox.kind')}</th>
              <th>{t('mailbox.server')}</th>
              <th>{t('mailbox.state')}</th>
              {mayEdit && <th aria-label={t('mail.adm.actions')} />}
            </tr>
          </thead>
          <tbody>
            {list.map((box) => (
              <tr key={box.id}>
                <td className="strong">{box.name}</td>
                <td>{box.kind === 'personal' ? t('mailbox.kind.personalOf', { owner: box.ownerName ?? '—' }) : t('mailbox.kind.shared')}</td>
                <td className="mono t-sm">{box.host ? `${box.username ?? ''}@${box.host}:${box.port ?? ''} / ${box.folder}` : '—'}</td>
                <td>
                  <OnOffBadge on={box.enabled} />{' '}
                  <span className="t-sm">{pollState(box, t, locale)}</span>
                </td>
                {mayEdit && (
                  <td>
                    <RowMenu label={t('mail.adm.actionsOf', { name: box.name })} items={[
                      { id: 'edit', label: t('mail.adm.edit'), icon: 'adm-edit', onSelect: () => setDialog(box) },
                      { id: 'poll', label: t('mailbox.pollNow'), icon: 'refresh', onSelect: () => poll.mutate(box) },
                      { id: 'delete', label: t('mail.adm.delete'), icon: 'trash', danger: true, onSelect: () => setRemoving(box) },
                    ]} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dialog && (
        <MailboxDialog
          mailbox={dialog === 'new' ? null : dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null)
            refresh()
          }}
        />
      )}
      <ConfirmModal
        open={removing != null}
        title={t('mailbox.delete.confirm', { name: removing?.name ?? '' })}
        confirmLabel={t('mail.adm.delete')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
      >
        {t('mailbox.delete.text')}
      </ConfirmModal>
    </section>
  )
}

function pollState(box: Mailbox, t: (key: string, params?: Record<string, string | number>) => string, locale: string): string {
  const when = (value: string | null | undefined) => {
    if (value == null) return ''
    const moment = new Date(value)
    return Number.isNaN(moment.getTime()) ? value : moment.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')
  }
  switch (box.pollStatus) {
    case 'ok':
      return t('mailbox.status.ok', { at: when(box.lastPolledAt) })
    case 'failed':
      return t('mailbox.status.failed', { at: when(box.retryAfter), error: box.lastError ?? '' })
    case 'rejected':
      return t('mailbox.status.rejected', { error: box.lastError ?? '' })
    default:
      return t('mailbox.status.idle')
  }
}

function MailboxDialog({ mailbox, onClose, onSaved }: {
  mailbox: Mailbox | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t, user } = useApp()
  const formId = useId()
  const [name, setName] = useState(mailbox?.name ?? '')
  const [kind, setKind] = useState<MailboxKind>(mailbox?.kind ?? 'shared')
  const [owner, setOwner] = useState(mailbox?.ownerUserId ?? '')
  const [host, setHost] = useState(mailbox?.host ?? '')
  const [port, setPort] = useState<number | null>(mailbox?.port ?? 993)
  const [security, setSecurity] = useState<MailboxSecurity>(mailbox?.security ?? 'ssl')
  const [username, setUsername] = useState(mailbox?.username ?? '')
  const [password, setPassword] = useState('')
  const [folder, setFolder] = useState(mailbox?.folder ?? 'INBOX')
  const [enabled, setEnabled] = useState(mailbox?.enabled ?? false)
  const [error, setError] = useState<string | null>(null)
  // Owners are user accounts; the list needs system.user. Without it the field says so.
  const users = useQuery({
    queryKey: ['mailbox-owners'],
    queryFn: ({ signal }) => fetchAccessUsers('', signal),
    enabled: kind === 'personal' && can(user, 'system.user', 'read'),
  })
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        kind,
        ownerUserId: kind === 'personal' && owner !== '' ? owner : null,
        host: host.trim() || null,
        port,
        security,
        username: username.trim() || null,
        password: password === '' ? undefined : password,
        folder: folder.trim() || null,
        enabled,
      }
      return mailbox ? updateMailbox(mailbox.id, body) : createMailbox(body)
    },
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof ApiError ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="sheet"
      overline={t('mailbox.title')}
      title={mailbox ? t('mail.adm.edit') : t('mailbox.add')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending} form={formId}
                            disabled={name.trim() === ''} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('mailbox.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <Field label={t('mailbox.name')} htmlFor={`${formId}-name`} required>
          <input id={`${formId}-name`} className="input" maxLength={128} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label={t('mailbox.kind')} htmlFor={`${formId}-kind`}>
            <select id={`${formId}-kind`} className="input" value={kind} onChange={(e) => setKind(e.target.value as MailboxKind)}>
              <option value="shared">{t('mailbox.kind.shared')}</option>
              <option value="personal">{t('mailbox.kind.personal')}</option>
            </select>
          </Field>
          {kind === 'personal' && (
            <Field label={t('mailbox.owner')} htmlFor={`${formId}-owner`}
                   help={can(user, 'system.user', 'read') ? undefined : t('mailbox.owner.noAccess')}>
              <select id={`${formId}-owner`} className="input" value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">—</option>
                {mailbox?.ownerUserId && !(users.data ?? []).some((candidate) => candidate.id === mailbox.ownerUserId) && (
                  <option value={mailbox.ownerUserId}>{mailbox.ownerName ?? mailbox.ownerUserId}</option>
                )}
                {(users.data ?? []).filter((candidate) => candidate.active).map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>{candidate.fullName} ({candidate.username})</option>
                ))}
              </select>
            </Field>
          )}
          <Field label={t('mail.conn.host')} htmlFor={`${formId}-host`}>
            <input id={`${formId}-host`} className="input" value={host} onChange={(e) => setHost(e.target.value)} />
          </Field>
          <Field label={t('mail.conn.port')} htmlFor={`${formId}-port`}>
            <input id={`${formId}-port`} className="input" type="number" value={port ?? ''}
                   onChange={(e) => setPort(e.target.value === '' ? null : Number(e.target.value))} />
          </Field>
          <Field label={t('mail.conn.security')} htmlFor={`${formId}-security`}>
            <select id={`${formId}-security`} className="input" value={security}
                    onChange={(e) => setSecurity(e.target.value as MailboxSecurity)}>
              {(['ssl', 'starttls', 'none'] as const).map((code) => (
                <option key={code} value={code}>{t(`mail.conn.security.${code}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('mail.conn.username')} htmlFor={`${formId}-user`}>
            <input id={`${formId}-user`} className="input" value={username} onChange={(e) => setUsername(e.target.value)} />
          </Field>
          <Field label={t('mail.conn.password')} htmlFor={`${formId}-password`}
                 help={mailbox?.passwordSet ? t('mail.conn.password.set') : t('mail.conn.password.unset')}>
            <input id={`${formId}-password`} className="input" type="password" autoComplete="new-password"
                   value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label={t('mailbox.folder')} htmlFor={`${formId}-folder`}>
            <input id={`${formId}-folder`} className="input" value={folder} onChange={(e) => setFolder(e.target.value)} />
          </Field>
        </div>
        <Switch checked={enabled} label={t('mailbox.enabled')} onChange={setEnabled} />
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
